<script setup lang="ts">
import { ref, onBeforeMount, onMounted } from 'vue'
import type { IBlog_Data } from '@/components/content-app/interface/blog/IBlog'
import Banner from '@/components/blog/category/CategoryItemBanner.vue'
import BlogSocialSharing from '@/components/blog/BlogSocialSharing.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogsService from '@/services/content-app/blog.service'
import ContentService, {
  scrollToElement,
  type IHeadingLink
} from '@/utils/content-app/content.utils'
import SwitchingModal from '@/components/organisation/shared/SwitchingModal.vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const _BlogsService = new BlogsService()
const blog = ref<IBlog_Data | null>(null)
const relatedBlogs = ref<IBlog_Data[]>([])
const _contentService = new ContentService()
const headingLinks = ref([] as IHeadingLink[])
const content = ref<string>('')

onBeforeMount(async () => {
  const data: IBlog_Data[] = await _BlogsService.getSearchedBlog(route.params.blogId as string)
  blog.value = data[0]

  if (blog.value.attributes?.blog_tags.data.length) {
    const related = blog.value.attributes.blog_tags.data.map((item) => item.attributes.name)
    relatedBlogs.value = await _BlogsService.getRelatedBlogs(related[0])

    const blogId = blog.value.id
    relatedBlogs.value = relatedBlogs.value.filter((item) => item.id !== blogId)
  }
  if (blog.value) {
    const htmlWithConvertedIframes = _contentService.convertOembedToIframe(
      blog.value.attributes.content
    )
    let { modifiedHtml, linksArray } =
      _contentService.injectIdIntoHtml_returnHtmlAndLinks(htmlWithConvertedIframes)
    content.value = modifiedHtml
    headingLinks.value = linksArray
  }
})

/**
 * load dynamic scripts
 */
const loadDynamicScripts = () => {
  const scripts = document.querySelectorAll('.embeds > script')
  const srcs = Array.from(scripts).map((script) => (script as HTMLScriptElement).src)
  srcs.forEach((src) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    document.body.appendChild(script)
  })
}

onMounted(() => {
  blog.value && loadDynamicScripts()
})
</script>
<template>
  <div v-if="blog">
    <Banner :blog="blog" />
    <div class="px-5 md:px-11 xl:px-44">
      <div class="flex flex-col pt-9 sm:pb-24 lg:flex-row lg:gap-x-20 lg:py-14">
        <div class="lg:w-1/4">
          <div class="grid grid-cols-2 grid-rows-[auto,2fr] lg:block lg:grid-cols-1">
            <!-- <div class="order-2 row-start-2 flex items-start gap-x-6 lg:items-center">
              <button class="flex items-center dark:text-[B6BAC3]">
                <FontAwesomeIcon icon="fa-hands-clapping" class="mr-1 h-6 w-6" />1.5K
              </button>
              <button class="dark:text-[B6BAC3]">
                <FontAwesomeIcon icon="fa-bookmark fa-regular" class="h-6 w-6" />
              </button>
            </div> -->
            <div
              v-if="headingLinks.length"
              class="md:rounded-0 order-1 col-span-2 mb-6 rounded-[20px] md:col-span-1 md:mb-0"
            >
              <div class="flex justify-between border-b border-black/10 py-4 md:border-b-0 md:p-0">
                <h6 class="text-base font-medium dark:text-[B6BAC3]">TABLE OF CONTENTS</h6>
              </div>

              <div class="mb-9 mt-2.5 hidden w-24 border-b border-text-brand md:block lg:my-6" />
              <div class="mb-3 py-6 font-light md:mb-12 md:p-0 lg:mb-14 dark:text-[B6BAC3]">
                <div
                  @click.prevent="scrollToElement(`#${headingLink.linkId}`)"
                  v-for="(headingLink, index) in headingLinks"
                  :key="index"
                  :class="index !== 0 ? 'py-1' : 'pb-1'"
                  class="block cursor-pointer break-words"
                >
                  {{ headingLink.linkName }}
                </div>
              </div>
            </div>

            <div class="order-3 row-start-2">
              <div class="w-max lg:ml-0">
                <div class="my-6 hidden w-24 border-b border-text-brand lg:block" />
                <blog-social-sharing></blog-social-sharing>
                <p class="mt-5 hidden font-light md:block lg:mt-6 dark:text-[B6BAC3]">
                  Share this article
                </p>
              </div>
            </div>
          </div>

          <div
            class="mt-12 flex items-center gap-x-6 md:mt-14 md:gap-x-10 lg:mt-32 lg:block lg:gap-0"
          >
            <div class="h-20 w-20 rounded-full bg-[#cccccc] md:h-24 md:w-24 lg:h-32 lg:w-32">
              <img
                class="h-full w-full rounded-full"
                :src="blog.attributes.author?.data.attributes.thumbnail.data.attributes.url"
                alt="avatar"
              />
            </div>
            <div class="w-8/12 lg:mt-7 lg:w-full">
              <p class="text-sm font-medium text-[#8C8C8C]">AUTHOR</p>
              <p class="text-base font-medium dark:text-[B6BAC3]">
                {{ blog.attributes.author.data.attributes.firstName }}
                {{ blog.attributes.author.data.attributes.lastName }}
              </p>
              <p class="text-[13px] font-light dark:text-[B6BAC3]">
                {{ blog.attributes.author.data.attributes.description }}
              </p>
            </div>
          </div>
        </div>
        <div class="mt-12 lg:mt-0 lg:w-3/4">
          <div class="doc_content" v-html="content" />
          <!-- <div class=" px-6 lg:px-0 flex items-center justify-between">
            <button
              class="btn btn-primary flex h-12 w-12 items-center justify-center rounded-full md:h-auto md:w-fit md:px-8 md:py-4"
            >
              <FontAwesomeIcon icon="fa-arrow-left" class="text-lg text-white md:mr-2.5" />
              <span class="hidden md:inline">PREV POST</span>
            </button>
            <button
              class="btn btn-primary flex h-12 w-12 items-center justify-center rounded-full md:h-auto md:w-fit md:px-8 md:py-4"
            >
              <span class="hidden md:inline">NEXT POST</span>
              <FontAwesomeIcon icon="fa-arrow-right" class="text-lg text-white md:ml-2.5" />
            </button>
          </div> -->
        </div>
      </div>
      <div class="mb-24" v-if="relatedBlogs.length">
        <div
          class="flex items-start justify-between border-b border-[#ECECEC] pb-6 xl:pb-7 dark:border-[#25272c]"
        >
          <h3 class="text-[26px] font-medium leading-none md:text-[32px] dark:text-[#D8DBDF]">
            Related Topics
          </h3>
          <!-- <button class="font-medium dark:text-[#D8DBDF]">view all</button> -->
        </div>
        <div class="mt-6 grid gap-x-7 md:grid-cols-3 xl:mt-7">
          <BlogCard :blog="item" v-for="item in relatedBlogs" :key="item.attributes.url" />
        </div>
      </div>
    </div>
  </div>
  <SwitchingModal :isBlogPage="true" v-else />
</template>
